<template>
  <div>
    <!-- On boucle sur l'objet prices reçu en props -->
    <div class="grid gap-6 sm:grid-cols-1 md:grid-cols-3 mb-8">
      <div
        v-for="(product, productId) in prices"
        :key="productId"
        class="bg-white border rounded-lg shadow p-6 flex flex-col items-center"
      >
        <h3 class="text-lg font-medium mb-2">{{ product.label }}</h3>
        <p class="text-xl font-semibold mb-4">{{ formatPrice(product.price) }} €</p>
        <button
          @click="selectProduct(productId)"
          class="bg-pink-500 text-white px-4 py-2 rounded-md hover:bg-pink-600"
        >
          Commander
        </button>
      </div>
    </div>

    <!-- Formulaire de paiement Stripe (affiché si un produit est sélectionné) -->
    <div v-if="selectedProductId" class="modal-content" @click.stop>
      <div v-if="failureMessage" class="mb-4 text-red-600">
        {{ failureMessage }}
      </div>

      <!-- Élément de paiement -->
      <div id="payment-element"></div>

      <!-- Checkbox d'acceptation des CGV -->
      <div class="flex items-center mt-4 mb-4">
        <input
          type="checkbox"
          id="cgv"
          v-model="cgvAccepted"
          required
          class="h-4 w-4 text-blue-600 border-gray-300 rounded"
        />
        <label for="cgv" class="ml-2 text-gray-700">
          J'accepte les 
          <a href="/cgv" target="_blank" class="text-blue-500 underline">Conditions Générales de Vente</a>
        </label>
      </div>

      <!-- Bouton de paiement -->
      <button
        @click="processSubscription"
        :disabled="!isLoaded || !cgvAccepted"
        :class="{ 
          'bg-pink-500 text-white border border-transparent rounded-md py-2 px-4 inline-flex justify-center text-sm leading-5 hover:bg-pink-600 focus:outline-none transition duration-150 ease-in-out': true,
          'opacity-50 cursor-not-allowed': !isLoaded || !cgvAccepted
        }"
      >
        <span v-if="isLoaded">
         Payer {{ formattedSelectedPrice }} €
        </span>
        <span v-else>
          Chargement...
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { loadStripe } from '@stripe/stripe-js'

export default {
  props: {
    prices: {
      type: Object,
      required: true
    },
    returnUrl: {
      type: String,
      default: '/'
    }
  },
  data() {
    return {
      stripe: null,
      elements: null,
      cardElement: null,
      setupIntentClientSecret: null,
      isLoaded: false,
      failureMessage: '',
      selectedProductId: null,
      cgvAccepted: false
    }
  },
  computed: {
    formattedSelectedPrice() {
      if (!this.selectedProductId) return ''
      const product = this.prices[this.selectedProductId]
      return product ? this.formatPrice(product.price) : ''
    }
  },
  methods: {
    formatPrice(value) {
      return Number(value).toFixed(2)
    },

    async selectProduct(productId) {
      try {
        this.selectedProductId = productId
        this.failureMessage = ''
        this.isLoaded = false
        this.cgvAccepted = false

        this.stripe = await loadStripe(process.env.MIX_STRIPE_KEY)

        const { data } = await axios.post('/payments/create-setup-intent', {
          price_id: this.selectedProductId
        })
        
        this.setupIntentClientSecret = data.setup_intent_client_secret

        this.elements = this.stripe.elements({
          clientSecret: this.setupIntentClientSecret
        })

        this.cardElement = this.elements.create('payment', {
          defaultValues: {
            billingDetails: {
              address: { country: 'BE' }
            }
          }
        })

        this.cardElement.mount('#payment-element')

        this.isLoaded = true
      } catch (error) {
        console.error(error)
        this.failureMessage = "Impossible d'initialiser le paiement."
      }
    },

    async processSubscription() {
      if (!this.cgvAccepted) {
        this.failureMessage = "Vous devez accepter les CGV."
        return
      }

      this.isLoaded = false
      try {
        const result = await this.stripe.confirmSetup({
          elements: this.elements,
          redirect: 'if_required'
        })

        if (result.error) {
          this.failureMessage = result.error.message
          this.isLoaded = true
          return
        }

        const setupIntent = result.setupIntent
        if (setupIntent.status === 'succeeded') {
          const paymentMethodId = setupIntent.payment_method

          const response = await axios.post('/payments/create-subscription', {
            price_id: this.selectedProductId,
            payment_method: paymentMethodId
          })

          window.location.href = this.returnUrl
        } else {
          this.failureMessage = "Une action supplémentaire est requise, paiement non finalisé."
        }

      } catch (err) {
        console.error(err)
        this.failureMessage = "Erreur lors de la finalisation de l'abonnement."
      } finally {
        this.isLoaded = true
      }
    }
  }
}
</script>

<style scoped></style>